/**
 * Tolerant taps: the rule that turns a press-and-lift into an activation when
 * the browser will not (decision 0014).
 *
 * Measured on the ET60, 2026-09-02, over DevTools: of 61 taps on real
 * controls, 16 produced no click. Every tap that clicked had drifted 0–8 px
 * between touchdown and lift; every lost one had drifted 8–13 px. Chrome's
 * touch slop on Android is 8 dp — 8 CSS px on this tablet — and a finger
 * pressing this glass rolls about 10 px as it flattens, so a quarter of normal
 * presses are reclassified as the start of a scroll: early in the press the
 * pointer is CANCELLED, late in the press it is released without a click.
 * Nothing in the page can change the slop; the page can only recognise the
 * tap itself.
 *
 * The slop here is three times the browser's: comfortably above the drift of
 * a pressing finger, far below the shortest deliberate scroll in the log
 * (227 px). A long press is left alone so the browser's own long-press
 * meanings survive, and a mouse is judged by the same geometry but never recovered, because a mouse
 * click is never lost.
 */

/** What counts as a control worth recovering a tap for. */
export const TAP_CONTROLS = 'button, a[href], [role="button"], .pressable'

/** Farther than this between touchdown and lift is a scroll, not a tap. */
export const TAP_SLOP_PX = 24
/** Longer than this is a long press, which is the browser's to interpret. */
export const TAP_MAX_MS = 700
/** How long the browser gets to deliver its own click before the page acts. */
export const NATIVE_CLICK_GRACE_MS = 80
/** A trusted click on the same control this soon after a recovered one is the late native twin. */
export const DEDUPE_MS = 300

/**
 * Dispatched on the control, bubbling, the moment a press-and-lift is judged
 * a tap — whether the browser is about to click it or the page will. The tap
 * sound plays to this, so a scroll that began on a control never ticks.
 */
export const TAP_EVENT = 'dockautomation:tap'

export type TapSample = {
  downX: number
  downY: number
  upX: number
  upY: number
  downT: number
  upT: number
}

export type TapVerdict = 'activate' | 'too-far' | 'too-long'

/**
 * Geometry and time only, for any pointer: a mouse press dragged off a button
 * is no more a tap than a finger's. Whether a tap then needs RECOVERING is the
 * plugin's call — a mouse click is never lost, a touch one is.
 */
export function judgeTap(sample: TapSample, slopPx = TAP_SLOP_PX, maxMs = TAP_MAX_MS): TapVerdict {
  if (sample.upT - sample.downT > maxMs) return 'too-long'
  const dx = sample.upX - sample.downX
  const dy = sample.upY - sample.downY
  if (Math.hypot(dx, dy) > slopPx) return 'too-far'
  return 'activate'
}

/** The narrowest thing the rule needs from an element, so it is testable without a DOM. */
export type TapControl = {
  hasAttribute: (name: string) => boolean
  getAttribute: (name: string) => string | null
}
export type TapTarget = { closest: (selector: string) => TapControl | null }

/**
 * The control a press on `target` is a press ON — or null when there is none,
 * or when it is disabled. A disabled control did nothing natively and must do
 * nothing here either.
 */
export function findTapControl<T extends TapControl>(target: { closest: (selector: string) => T | null } | null): T | null {
  if (!target) return null
  const control = target.closest(TAP_CONTROLS)
  if (!control) return null
  if (control.hasAttribute('disabled')) return null
  if (control.getAttribute('aria-disabled') === 'true') return null
  return control
}
